import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  ViedocButtonElement,
  ViedocDialogElement,
} from "../../../../secure/elements/ViedocReactElements";
import * as DocumentService from "../../../../secure/services/documentService";
import * as TemplateService from "../../../../secure/services/templateService";
import * as templateActions from "../../../../store/actions/templateActions";
import * as loaderActions from "../../../../store/actions/loaderActions";
import * as UtilsHelper from "../../../../../utils/utilsHelper";
import { StorageHelper } from "../../../../../utils/storageHelper";
import _ from "underscore";
import { BlobServiceClient } from "@azure/storage-blob";
import ProgressBar from "../../shared/widgets/ProgressBar";
import Loader from "../../shared/Loader/Loader";
import ManageTemplatePopUp from "./ManageTemplatePopUp";
import DeleteDocumentPopUp from "../../shared/DeleteDocumentPopUp/DeleteDocumentPopUp";
import APP_CONST from "../../../shared/constants";

const Templates = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { orgGuid, userList, isReadOnlyAdmin, isZoneLoading } = useSelector(
    (store) => store.zone
  );
  const {
    orgTemplates,
    studyTemplates,
    fullDateTimePattern,
    dateLocale,
    isLoading,
    isExporting,
    exportingList,
    fileScreeningStatus,
    importStatus,
    errorStatusMessage,
    isDeleting,
    isEditing,
    isCancelBeforeSuccess,
    selectedTempID,
    selectedLoading,
    loadingTempID,
    lockStatus,
    isEtmfLocked,
  } = useSelector((store) => store.template);

  const templateRef = useRef();
  const studyRef = useRef();
  const isAdminReadOnly = !isReadOnlyAdmin && !isEtmfLocked;
  const [isManageTemplateVisible, setIsManageTemplateVisible] = useState(false);
  const [templateInfo, setTemplateInfo] = useState();
  const [importFileName, setImportFileName] = useState("");
  const [orgTempImporting, setOrgTempImporting] = useState({});
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState();
  const [editingTemplateId, setEditingTemplateId] = useState();
  let [cancelCount, setCancelCount] = useState(0);
  const [isDeletePopUpVisible, setIsDeletePopUpVisible] = useState(false);
  const [deletePayload, setDeletePayload] = useState({});
  const [isShowError, setIsShowError] = useState(false);

  useEffect(() => {
    if (importStatus === "cancelled") {
      setOrgTempImporting({});
      setIsProgressVisible(false);
    }
  }, [importStatus]);

  useEffect(() => {
    if (!_.isEmpty(fileScreeningStatus)) {
      const { loadFrom, loadTo } = fileScreeningStatus;
      handleUploadProgressBar(loadFrom, loadTo, fileScreeningStatus);
    }
    if (isCancelBeforeSuccess && cancelCount === 0) {
      dispatch(templateActions.getFileScreeningCancel(orgTempImporting));
      setCancelCount(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileScreeningStatus]);

  useEffect(() => {
    const { responseData, loadStatus } = orgTempImporting;
    if (loadStatus === "cancelled") {
      dispatch(templateActions.importTemplateFailed(responseData.result));
      dispatch(loaderActions.hideError());
    }

    if (
      responseData?.result?.status === undefined &&
      importStatus === "Failed"
    ) {
      setIsShowError(true);
      setIsProgressVisible(false);
    }

    if (orgTempImporting.percent === 100) {
      setTimeout(() => {
        if (!responseData.isError) {
          if (loadStatus !== "cancelled")
            dispatch(
              templateActions.importTemplateSuccess(responseData.result)
            );
          else
            dispatch(templateActions.importTemplateFailed(responseData.result));
          dispatch(loaderActions.hideError());
          setIsProgressVisible(false);
        } else {
          dispatch(templateActions.importTemplateFailed(responseData.result));
        }
        setTimeout(() => {
          setOrgTempImporting({});
        }, 500);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgTempImporting]);

  useEffect(() => {
    if (!_.isEmpty(orgGuid)) {
      dispatch(templateActions.getLockStatus());

      dispatch(templateActions.getAllTemplates(orgGuid));
      dispatch(templateActions.getTMFStructureData("onload"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgGuid]);

  const onHide = () => {
    dispatch(templateActions.getFileScreeningStatusFailed({}));
    dispatch(templateActions.importTemplateFailed({}));
    setIsShowError(false);
  };

  const renderNoTemplatesBlock = () => {
    return (
      <div className="no-templates pl-30 pt-20 pb-20 bb-1">
        <p>{t("ADMIN.TEMPLATES_NOTEMPLATESIMPORTED")}</p>
      </div>
    );
  };

  const manageTemplateHandler = (templateInformation) => {
    setIsManageTemplateVisible(true);
    setTemplateInfo(templateInformation);

    setDeletePayload({
      templateId: templateInformation.templateid,
      templateCategory: templateInformation.templatecategory,
      fileName: templateInformation.templatename,
    });
  };

  const exportTemplateHandler = (templateInformation) => {
    const { templateid, templatecategory, templatename } = templateInformation;
    const exportTempName = templatename.replace(/\.[^/.]+$/, "");
    dispatch(
      templateActions.exportTemplate(
        templateid,
        templatecategory,
        exportTempName
      )
    );
  };

  const templateSelectHandler = (data) => {
    if (isEtmfLocked) return;
    if (selectedTempID !== data.templateid) {
      const requestPayload = { templateId: data.templateid };
      dispatch(
        templateActions.selectTemplate(requestPayload, data.templatecategory)
      );
    }
  };

  const renderTemplateList = (templateList) => {
    return _.isEmpty(templateList)
      ? renderNoTemplatesBlock()
      : templateList.map((template) => (
          <div className="list-container pl-10 bb-2" key={template.templateid}>
            <div className="template-list">
              <div
                className={
                  selectedTempID === template.templateid
                    ? "selected-template template-sub-list"
                    : "template-sub-list"
                }
              >
                {(isDeleting && template.templateid === deletingTemplateId) ||
                (isEditing && template.templateid === editingTemplateId) ? (
                  <viedoc-spinner></viedoc-spinner>
                ) : (
                  <>
                    <viedoc-icon name="folder_unselected"></viedoc-icon>
                    {selectedTempID === template.templateid && (
                      <viedoc-icon name="check" />
                    )}
                  </>
                )}
              </div>
              <div className="pl-20">
                <p
                  title={UtilsHelper.fileExtensionRemoval(
                    template.templatename
                  )}
                  className="f17 ff-vmg pb-5 cursor-pointer"
                >
                  {UtilsHelper.fileExtensionRemoval(template.templatename)}
                </p>
                <p className="f14 ff-mg">
                  {`${template.status === APP_CONST.TEXT_RENAMED ? t("ADMIN.TEXT_RENAMED") : t("ADMIN.TEXT_IMPORTED")}
              ${UtilsHelper.getFormattedDateTime(
                fullDateTimePattern,
                template.lastmodifiedon
                  ? template.lastmodifiedon
                  : template.importedon,
                dateLocale
              )}
              ${t("TMF.TEXT_BY")}
              ${UtilsHelper.findUserName(
                userList,
                template.lastmodifiedby
                  ? template.lastmodifiedby
                  : template.importedby
              )}`}
                </p>
                <p className="f14 ff-mg">
                  {template.zoneCount}{" "}
                  <span className="gray-60">{`${t(
                    "ADMIN.TEXT_ZONES"
                  )} - `}</span>
                  {template.sectionCount}{" "}
                  <span className="gray-60">{`${t(
                    "ADMIN.TEXT_SECTIONS"
                  )} - `}</span>
                  {template.artifactCount}{" "}
                  <span className="gray-60">{`${t(
                    "ADMIN.TEXT_ARTIFACTS"
                  )}`}</span>
                </p>
              </div>
            </div>

            <div className="template-buttons pr-20">
              <div className="action-buttons pr-25">
                {isAdminReadOnly && (
                  <>
                    <p
                      title={t("ADMIN.ADMINTAB_MANAGE")}
                      className="cursor-pointer ff-mg"
                      onClick={() => manageTemplateHandler(template)}
                    >
                      {t("ADMIN.ADMINTAB_MANAGE")}
                    </p>
                    <p style={{ color: "var(--color-blue-15)" }}>
                      {" "}
                      &nbsp;&nbsp;{"|"}&nbsp;&nbsp;
                    </p>
                  </>
                )}
                {isExporting && exportingList.includes(template.templateid) ? (
                  <viedoc-spinner
                    size="small"
                    class="export-spinner"
                  ></viedoc-spinner>
                ) : (
                  <p
                    title={t("ADMIN.ADMINTAB_EXPORT")}
                    className="cursor-pointer ff-mg"
                    onClick={() => exportTemplateHandler(template)}
                  >
                    {t("ADMIN.ADMINTAB_EXPORT")}
                  </p>
                )}
              </div>
              {!(isDeleting && template.templateid === deletingTemplateId) && (
                <>
                  {lockStatus !== APP_CONST.ADMIN_TEMPLATE_STATUS[1] && (
                    <div
                      className={
                        isEtmfLocked
                          ? "select-button cursor-nodrop"
                          : selectedTempID === template.templateid
                          ? "selected select-button cursor-nodrop"
                          : "select-button"
                      }
                      title={t("ADMIN.ADMINTAB_SELECT")}
                      onClick={() => templateSelectHandler(template)}
                    >
                      {selectedLoading &&
                      loadingTempID === template.templateid ? (
                        <viedoc-spinner size="small"></viedoc-spinner>
                      ) : (
                        <>
                          <viedoc-icon name="check" slot="prefix"></viedoc-icon>
                          <p>
                            {selectedTempID === template.templateid
                              ? t("ADMIN.TEXT_SELECTED")
                              : t("ADMIN.ADMINTAB_SELECT")}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ));
  };

  const handleUploadProgressBar = (from, to, fileInformation) => {
    let fileInfo = {
      ...fileInformation,
    };
    for (let i = from; i <= to; ++i) {
      // eslint-disable-next-line no-loop-func
      setTimeout(function () {
        let LoadingCount = i * 4;
        fileInfo = {
          ...fileInfo,
          percent: LoadingCount,
        };
        setOrgTempImporting(fileInfo);
      }, 200 * i);

      if (i === to) return fileInfo;
    }
    return fileInfo;
  };

  const getGuid = async () => {
    try {
      const guidResp = await DocumentService.doGetDocumentGuid();
      if (guidResp.status === 200) {
        return guidResp.data.result.documentguid;
      } else {
        return null;
      }
    } catch (e) {}
  };

  const uploadWithSASGeneration = async (file, guid) => {
    try {
      const sasTokenResult = await TemplateService.doFetchSAStoken();
      const { data } = sasTokenResult;
      const studyConfig = StorageHelper.getStudyConfig();
      if (!data.isError) {
        const { sastoken } = data.result;
        const decodedToken = atob(sastoken);
        const containerName = decodedToken.substring(
          decodedToken.lastIndexOf("/") + 1,
          decodedToken.lastIndexOf("?")
        );

        const splitDecodeURL = decodedToken.split(`/${containerName}?`);
        const sasString = `${splitDecodeURL[0]}/?${splitDecodeURL[1]}`;
        const containerURL = new BlobServiceClient(sasString);
        const containerClient = containerURL.getContainerClient(containerName);
        const updataGuid = `template_${guid}_${studyConfig.studyid}`;

        const blockBlobClient = containerClient.getBlockBlobClient(updataGuid);
        const config = {
          metadata: {
            // filename: file.name,
          },
        };
        return blockBlobClient.uploadBrowserData(file, config);
      }

      // }
    } catch (error) {
      return "error";
    }
  };

  const importTemplateHandler = async (event, category) => {
    let allowedFileTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "",
    ];

    const files = event.target.files;
    let requestPayload, fileInfo;
    if (!allowedFileTypes.includes(files[0].type)) {
      dispatch(
        loaderActions.showError(
          t("ADMIN.TOAST_INVALIDFILE"),
          "error",
          "warning"
        )
      );
    } else {
      dispatch(loaderActions.hideError());

      setImportFileName(files[0].name);
      requestPayload = {
        type: "Template",
        fileName: files[0].name,
        category: category,
        status: "Upload In-Progress",
        statusMessage: "Upload In-Progress",
        orgId: orgGuid,
      };
      fileInfo = {
        fileName: files[0].name,
        loadingText: "Uploading...",
        percent: 0,
        category: category,
        id: files[0].lastModified,
      };
      setOrgTempImporting(fileInfo);
      setIsProgressVisible(true);
      setCancelCount(0);
      dispatch(templateActions.getFileScreeningCancelBeforeSuccess(false));

      fileInfo = handleUploadProgressBar(1, 6, fileInfo);
      const guid = await getGuid();

      try {
        // eslint-disable-next-line no-unused-vars
        const azureData = await uploadWithSASGeneration(files[0], guid);
        fileInfo = handleUploadProgressBar(6, 12, {
          ...fileInfo,
          fileGuid: guid,
        });
        requestPayload.fileGuid = guid;
        try {
          const fileScreening = await DocumentService.doFileScreening(
            requestPayload
          );
          const { isError } = fileScreening.data;
          if (!isError) {
            dispatch(
              templateActions.getFileScreeningStatus(
                guid,
                fileInfo,
                12,
                files[0].lastModified
              )
            );
          } else {
            fileInfo = {
              ...fileInfo,
              responseData: {
                isError: true,
                result: fileScreening.data,
              },
            };
            handleUploadProgressBar(12, 25, fileInfo);
          }
        } catch (error) {
          const errorResult = error.response || error.request;
          fileInfo = {
            ...fileInfo,
            responseData: {
              isError: true,
              result: errorResult.data,
            },
          };
          handleUploadProgressBar(6, 25, fileInfo);
        }
      } catch (e) {
        fileInfo = {
          ...fileInfo,
          responseData: {
            isError: true,
            result: null,
          },
        };
        handleUploadProgressBar(6, 25, fileInfo);
      }
    }
  };

  const importCancelHandler = () => {
    const { fileGuid } = orgTempImporting;
    dispatch(templateActions.handleTemplateCancel(true));
    if (fileGuid) {
      setIsProgressVisible(false);
      dispatch(templateActions.getFileScreeningCancel(orgTempImporting));
      setOrgTempImporting({});
    } else {
      setIsProgressVisible(false);
      dispatch(templateActions.getFileScreeningCancelBeforeSuccess(true));
      setOrgTempImporting({});
    }
  };

  const renderImportProgress = () => {
    return (
      <div className="template-upload-progress f15">
        <div className="ml-30">{importFileName}</div>
        <div className="d-flex align-center">
          <viedoc-spinner
            size="small"
            class="export-spinner mr-10"
          ></viedoc-spinner>
          <ProgressBar
            progress={orgTempImporting.percent || 0}
            className="progress"
          />
          {orgTempImporting.percent !== 100 ? (
            <div
              title={t("TMF.BUTTON_CANCELBTN")}
              className="cancel-button ml-10"
              onClick={(e) => importCancelHandler()}
            >
              <viedoc-icon name="delete" />
            </div>
          ) : (
            <div className="ml-10 mr-30"></div>
          )}
        </div>
      </div>
    );
  };

  const renderOrgTemplates = () => {
    return (
      <>
        <div className="org-templates pt-20 pl-30 bb-2 d-flex jc-space-btw">
          <div>
            <p className="f21">
              {`${t("ADMIN.TEMPLATES_ORGANIZATION")} 
              ${t("ADMIN.TEMPLATES_TEMPLATESS")}`}
            </p>
            <p className="pb-20 f16">
              {`${orgTemplates.length} ${t(
                orgTemplates.length > 1
                  ? "ADMIN.ADMINTAB_TEMPLATES"
                  : "ADMIN.TEMPLATES_TEMPLATE_S"
              ).toLocaleLowerCase()}`}
            </p>
          </div>
          {!isProgressVisible && isAdminReadOnly && (
            <div className="pr-20" title={t("ADMIN.TEMPLATES_IMPORT")}>
              <ViedocButtonElement
                variant="info"
                size="small"
                onClick={() => templateRef.current.click()}
              >
                {t("ADMIN.TEMPLATES_IMPORT")}
              </ViedocButtonElement>
              <input
                type="file"
                ref={templateRef}
                className="d-sm-none"
                onChange={(e) =>
                  importTemplateHandler(e, "Organization template")
                }
              />
            </div>
          )}
        </div>
        <div>
          {isProgressVisible &&
            !_.isEmpty(orgTempImporting) &&
            orgTempImporting.category === "Organization template" &&
            renderImportProgress()}
          {renderTemplateList(orgTemplates)}
        </div>
      </>
    );
  };

  const renderStudyTemplates = () => {
    return (
      <>
        <div className="org-templates pt-20 pl-30 bb-2 d-flex jc-space-btw">
          <div>
            <p className="f21">{`${t("ADMIN.TEMPLATES_STUDY")} 
              ${t("ADMIN.TEMPLATES_TEMPLATESS")}`}</p>
            <p className="pb-20 f16">
              {`${studyTemplates.length} ${t(
                studyTemplates.length > 1
                  ? "ADMIN.ADMINTAB_TEMPLATES"
                  : "ADMIN.TEMPLATES_TEMPLATE_S"
              ).toLocaleLowerCase()}`}
            </p>
          </div>
          {!isProgressVisible && isAdminReadOnly && (
            <div className="pr-20" title={t("ADMIN.TEMPLATES_IMPORT")}>
              <ViedocButtonElement
                variant="info"
                size="small"
                onClick={() => studyRef.current.click()}
              >
                {t("ADMIN.TEMPLATES_IMPORT")}
              </ViedocButtonElement>
              <input
                type="file"
                ref={studyRef}
                className="d-sm-none"
                onChange={(e) => importTemplateHandler(e, "Study template")}
              />
            </div>
          )}
        </div>
        <div>
          {isProgressVisible &&
            !_.isEmpty(orgTempImporting) &&
            orgTempImporting.category === "Study template" &&
            renderImportProgress()}
          {renderTemplateList(studyTemplates)}
        </div>
      </>
    );
  };

  const downloadFile = () => {
    UtilsHelper.errorDownloadFile(
      errorStatusMessage?.responseException?.exceptionMessage,
      `${t("ADMIN.TOAST_IMPORTERRORS")}.txt`
    );
  };

  const renderImportError = () => {
    return (
      <ViedocDialogElement
        open
        preventCloseByClick
        class="import-error"
        onHide={(e) => onHide(e)}
      >
        <section>
          <viedoc-icon name="warning"></viedoc-icon>
          <h2>
            {`${t("ADMIN.TOAST_IMPORTSOMETHINGWENTWRONG")} ${t(
              "ADMIN.TOAST_IMPORTERRORS"
            )} ${t("ADMIN.TOAST_FORMOREINFO")}`}
          </h2>
        </section>
        <div slot="footer">
          <ViedocButtonElement
            variant="neutral"
            size="small"
            onClick={downloadFile}
            title={`${t("TMF.TEXT_DOWNLOAD")} ${t("ADMIN.TOAST_IMPORTERRORS")}`}
          >
            {`${t("TMF.TEXT_DOWNLOAD")} ${t("ADMIN.TOAST_IMPORTERRORS")}`}
          </ViedocButtonElement>
        </div>
      </ViedocDialogElement>
    );
  };

  return (
    <div className="Tmf-admin-template">
      {isLoading || isZoneLoading ? (
        <Loader />
      ) : (
        <>
          {isManageTemplateVisible && (
            <ManageTemplatePopUp
              templateInfo={templateInfo}
              setIsManageTemplateVisible={setIsManageTemplateVisible}
              setDeletingTemplateId={setDeletingTemplateId}
              setEditingTemplateId={setEditingTemplateId}
              setIsDeletePopUpVisible={setIsDeletePopUpVisible}
            />
          )}
          {isDeletePopUpVisible && (
            <DeleteDocumentPopUp
              deletePayload={deletePayload}
              setIsDeletePopUpVisible={setIsDeletePopUpVisible}
              fromTo="Templates"
            />
          )}

          <div className="pl-25 pb-25 bb-2">
            <p className="f24 ff-vmg">{t("ADMIN.ADMINTAB_TEMPLATES")}</p>
          </div>
          {renderOrgTemplates()}
          {renderStudyTemplates()}
          {isShowError && renderImportError()}
        </>
      )}
    </div>
  );
};

export default Templates;
